import { Injectable, Injector, inject } from '@angular/core';
import { Router, CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { MsalService } from '@azure/msal-angular';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard {
  constructor(
    private authService: AuthService, 
    private router: Router,
    private msalService: MsalService
  ) {}

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    // Wait for MSAL to be initialized
    try {
      await this.msalService.instance.initialize();
      
      // Handle redirect promise FIRST to prevent interaction_in_progress errors
      try {
        await this.msalService.instance.handleRedirectPromise();
      } catch (error: any) {
        // If interaction_in_progress, wait and retry
        if (error?.errorCode === 'interaction_in_progress') {
          await new Promise(resolve => setTimeout(resolve, 1000));
          try {
            await this.msalService.instance.handleRedirectPromise();
          } catch (retryError) {
            // Ignore retry errors
          }
        }
      }
    } catch (error) {
      // MSAL already initialized or error - continue
    }
    
    // Check if MSAL account exists (handles redirect case)
    const accounts = this.msalService.instance.getAllAccounts();
    const activeAccount = this.msalService.instance.getActiveAccount();
    
    // If account exists but not set as active, set it
    if (accounts.length > 0 && !activeAccount) {
      this.msalService.instance.setActiveAccount(accounts[0]);
      // Give AuthService a moment to process
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // Check authentication status
    if (accounts.length > 0) {
      // Account exists - check if AuthService has processed it
      if (!this.authService.isAuthenticated()) {
        // Account exists but AuthService hasn't initialized yet
        // Trigger checkAuthStatus manually
        await this.authService.checkAuthStatus();
        // Wait a bit more for it to process user info
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      // If still not authenticated but account exists, allow access
      // AuthService will catch up
      if (!this.authService.isAuthenticated() && accounts.length > 0) {
        // Don't redirect if we're processing a redirect
        if (state.url.includes('#') || state.url.includes('code=')) {
          return true; // Allow MSAL to process redirect
        }
        return true; // Account exists, allow access
      }
    }
    
    // No account and not authenticated - redirect to login
    if (!this.authService.isAuthenticated() && accounts.length === 0) {
      // Don't redirect if we're already on login page or processing a redirect
      if (state.url !== '/login' && !state.url.includes('#') && !state.url.includes('code=')) {
        this.router.navigate(['/login']);
      }
      return false;
    }

    // Check for required roles
    const requiredRoles = route.data['roles'] as string[];
    if (requiredRoles && requiredRoles.length > 0) {
      // Wait for user info to be loaded (with retries - API call might take time)
      let user = this.authService.getUser();
      const maxRetries = 15;
      let retryCount = 0;
      
      // Retry up to 15 times (3 seconds total) to wait for user info to load
      while (!user && retryCount < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 200));
        user = this.authService.getUser();
        retryCount++;
      }
      
      if (!user) {
        // User info not loaded after waiting, but account exists - redirect to unauthorized
        // Don't redirect if we're already on unauthorized page to avoid loops
        if (state.url !== '/unauthorized' && !state.url.includes('#') && !state.url.includes('code=')) {
          this.router.navigate(['/unauthorized']);
        }
        return false;
      }

      const hasRequiredRole = requiredRoles.some(role => {
        switch (role) {
          case 'admin':
            return user.isAdmin;
          case 'pm':
            return user.isAdmin || user.isPM;
          case 'finance':
            return user.isAdmin || user.isFinance;
          case 'pc':
            return user.isAdmin || user.isPc;
          case 'software':
            return user.isSoftware;
          case 'accounting':
            return user.isAccounting;
          case 'marketing':
            return user.isMarketing;
          case 'sales':
            return user.isSales;
          case 'it':
            return user.isIt;
          case 'lnd':
            return user.isLnD;
          case 'operations':
            return user.isOperations;
          case 'support':
            return user.isSupport;
          default:
            return false;
        }
      });

      if (!hasRequiredRole) {
        // Don't redirect if we're already on unauthorized page to avoid loops
        if (state.url !== '/unauthorized' && !state.url.includes('#') && !state.url.includes('code=')) {
          this.router.navigate(['/unauthorized']);
        }
        return false;
      }
    }

    return true;
  }
}

export const authGuard: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const injector = inject(Injector);
  const authService = injector.get(AuthService);
  const router = injector.get(Router);
  const msalService = injector.get(MsalService);
  const guard = new AuthGuard(authService, router, msalService);
  return await guard.canActivate(route, state);
};

